import React, { useState, useEffect, useRef, useMemo } from 'react'
import classnames from 'classnames/bind'
import { find, isEmpty, findIndex, isString } from 'lodash-es'
import { useRouter } from 'next/router'
import { useMutation } from 'react-query'
import { differenceInMinutes, parseISO } from 'date-fns'
import { useTranslation } from 'next-i18next'
import { useCookies } from 'react-cookie'
import { useWindowHeight } from '@react-hook/window-size'

// Libs
import { useAppSelector, useAppDispatch } from '@/redux/hooks'
import { useLiveRoomData } from './hooks/useLiveRoomData'
import { useExternalProductData } from './hooks/useExternalProductData'
import useMedia from '@/hooks/useMedia'
import { getRoomHandUpList } from '@/methods/message/getRoomHandUpList'
import { handleUserHandUpStatus } from '@/methods/message/handleUserHandUpStatus'
import { handleUserConnectStatus } from '@/methods/message/handleUserConnectStatus'
import { setConnectStatus, setCountdownSecond, resetCountdownSecond, setIsShowUserOutPopup } from '@/redux/reducers/socket'
import { handleLoginOrRegister } from '@/methods/handleLoginOrRegister'
import { setUserRecord } from '@/api/setUserRecord'
import { getRoomSocketUsers } from '@/methods/message'
import useVideoAction from '@/hooks/useVideoAction'
import { handleCameraDevice, handleMicDevice } from '@/methods/message'
import handleRoutePushWithQuery from '@/methods/url/handleRoutePushWithQuery'
import vbchange, { diffNowMs } from '@/utils/vbchange'
// import { isAndroid, isIphone } from '@/utils/navigator'
import { handleSrcWithQuality } from '@/methods/handleSrcWithQuality'
import { handleGtmLiveInteraction } from '@/methods/handleGtmEvent'
import toCaseKeys, { CASES } from '@/utils/to-case-keys'
import { usePCHomeUUID } from './hooks/usePCHomeUUID'
import { pxecPostMessages } from '@/utils/pxec/postMessages'

// Components
import Desktop from './components/Desktop'
import Mobile from './components/Mobile'
import Preview from './components/Preview'
import Popup from '@/components/common/Popup'
// import RotateCover from './sharedComponents/RotateCover'
import SettingUserNamePopupContent from './components/SettingUserNamePopupContent'
import PortraitReleaseAgreementPopupContent from './components/PortraitReleaseAgreementPopupContent'

// Constants
import { ROOM_LIVE_TYPE, LIVE_SOURCE_TYPE } from '@/constants/liveRoom'
import { CONNECTION_STATUS } from '@/constants/socketEvent'
import { ROLE } from '@/constants/role'
import { GTM_LOCATION, GtmLiveInteractionType } from '@/constants/gtmEvent'
import { VALID_BRAND_NAME } from '@/constants/brand'
import { COOKIE_DATA } from '@/constants/cookieKey'
import { STORAGE_DATA } from '@/constants/storageData'

// Types
import { MerchantDataType } from '@/types/room'

// Styles
import styles from './style.module.scss'

// Context
export const LiveRoomContext = React.createContext({})

const cx = classnames.bind(styles)

type LiveRoomProps = {
  isStrawberry?: boolean
  isExternalProductData?: boolean
}

const LiveRoom = (props: LiveRoomProps) => {
  const { isStrawberry = false, isExternalProductData = false } = props

  const { t } = useTranslation()
  const viewportHeight = useWindowHeight()
  const loginPopupRef = useRef<any>(null)
  const checkPopopRef = useRef<any>(null)
  const waitingConnectPopopRef = useRef<any>(null)
  const connectingPopupRef = useRef<any>(null)
  const localStreamRef = useRef<any>(null)
  const userOutPopupRef = useRef<any>(null)
  const missingPopupRef = useRef<any>(null)
  const settingUserNamePopupRef = useRef<any>(null)
  const portraitReleaseAgreementPopupRef = useRef<any>(null)

  const roomData = useAppSelector((state) => state.room?.roomData)
  const memberData = useAppSelector((state) => state.member?.memberData)
  const socketData = useAppSelector((state) => state.socket)
  const hasMediaAccess = useAppSelector((state) => state.media.hasAccess)
  const isMicEnabled = useAppSelector((state) => state.media.isMicEnabled)
  const isCameraEnabled = useAppSelector((state) => state.media.isCameraEnabled)
  const fixedPlayerData = useAppSelector((state) => state.common.fixedPlayerData)

  const dispatch = useAppDispatch()

  const router = useRouter()
  const brandName: any = router?.query?.brandName || ''
  const roomId: string = isString(router.query?.roomId) ? router.query.roomId : ''
  const isValidBrandName = Object.values(VALID_BRAND_NAME).includes(brandName)
  const isHost = memberData?.memberInfoId === roomData?.memberInfoId || memberData?.role === ROLE.IT
  const [username, setUsername] = useState(null)

  const [cookies] = useCookies([COOKIE_DATA.PCHOME_UUID, COOKIE_DATA.PCHOME_NAME_EXPIRED])

  const closeSettingUsernamePopup = () => {
    setIsErrorFetchPchomeToken(false)
    setIsErrorUpdateNickname(false)
    settingUserNamePopupRef.current.setIsPopupOpen(false)
  }

  const {
    kolHeadshotUrl,
    dummyViews,
    kolName,
    liveType,
    coverPhotoUrl,
    memberInfoId: hostMemberInfoId,
    title,
    startTime,
    liveUrlInfo,
    liveSourceType,
    liveSource,
  } = roomData
  const merchantData: MerchantDataType = { kolHeadshotUrl, dummyViews, kolName, hostMemberInfoId }

  const { memberInfoId, nickName } = memberData
  const {
    socket,
    userToken,
    handUpList,
    micOnList,
    connectStatus,
    isReady: isConnectReady,
    isCountdowning,
    countdownSecond,
    hostData,
    localStream,
    isShowUserOutPopup,
  } = socketData
  localStreamRef.current = localStream

  const setUserRecordMutation = useMutation(setUserRecord)

  const isLive = liveType === ROOM_LIVE_TYPE.CURRENT
  const isPast = liveType === ROOM_LIVE_TYPE.PAST
  const isFuture = liveType === ROOM_LIVE_TYPE.FUTURE

  const isLogin = !!memberInfoId
  const isWaitingConnect = !isEmpty(find(handUpList, { uid: userToken }))
  const isMicConnecting = !isEmpty(find(micOnList, { uid: userToken }))

  const isWebRTCConnecting = connectStatus === CONNECTION_STATUS.CONNECTING

  const targetDate = parseISO(roomData.startTime)
  const currentDate = new Date()

  const minutesDifference = differenceInMinutes(targetDate, currentDate)
  const isLessThenOneHour = minutesDifference <= 60 // 目前定義 start time

  const strawberryProductList = typeof window !== 'undefined' ? toCaseKeys(window.STRAWBERRY_LIVE_PRODUCT, CASES.CAMEL) : []
  const authToken = typeof window !== 'undefined' ? window.localStorage.getItem(STORAGE_DATA.TOKEN) : ''

  const {
    isRoomInfoDataFetched: isRoomInfoDataFetchedRaw,
    roomLivingData: roomLivingDataRaw,
    productList: productListRaw,
    isRoomLivingDataSettled,
    setIsRoomLivingDataSettled,
    isRoomInfoDataSettled,
    setIsRoomInfoDataSettled,
  } = useLiveRoomData({ isLessThenOneHour, isExternalProductData })

  // PChome 合作案
  const {
    isLoadingFetchPChomeToken,
    isLoadingUpdateNickname,
    isErrorFetchPchomeToken,
    fetchPchomeTokenErrorData,
    isErrorUpdateNickname,
    updateNicknameErrorData,
    setIsErrorFetchPchomeToken,
    setIsErrorUpdateNickname,
  } = usePCHomeUUID({ userName: username, setUsername, closeSettingUsernamePopup, alwaysGuest: brandName === VALID_BRAND_NAME.PXEC, brandName })
  // edge case: 避免 viewport 高度很小時，介面跑版
  const isHeightAutoReferencePoint = 360

  // PChome 合作案
  useEffect(() => {
    const isHostStrict =
      (!isEmpty(memberData?.memberInfoId) && !isEmpty(roomData?.memberInfoId) && memberData?.memberInfoId === roomData?.memberInfoId) ||
      memberData?.role === ROLE.IT

    // PXEC 品牌：改為在 ChatInput focus 時才跳出 popup，這裡不自動跳出
    if (brandName === VALID_BRAND_NAME.PXEC) {
      return
    }

    // 其他品牌的原有邏輯
    if (router.isReady && isValidBrandName && isRoomInfoDataFetchedRaw && memberData.isFetched && !isHostStrict) {
      if (!isPast && !isFuture) {
        if (cookies?.[COOKIE_DATA.PCHOME_NAME_EXPIRED] && cookies?.[COOKIE_DATA.PCHOME_UUID] && authToken) {
          console.log('nick name not expired!')
        } else {
          settingUserNamePopupRef.current.setIsPopupOpen(true)
        }
      }
    } else if (router.isReady && isValidBrandName && isRoomInfoDataFetchedRaw) {
      if (!isPast && !isFuture) {
        if (isEmpty(authToken)) {
          settingUserNamePopupRef.current.setIsPopupOpen(true)
        }
      }
    }
  }, [
    router.isReady,
    isValidBrandName,
    isRoomInfoDataFetchedRaw,
    memberData.isFetched,
    memberData?.memberInfoId,
    roomData?.memberInfoId,
    authToken,
    isPast,
    isFuture,
    brandName,
  ])

  const isRoomInfoDataFetched = useMemo(() => isRoomInfoDataFetchedRaw, [isRoomInfoDataFetchedRaw])
  const roomLivingData = useMemo(() => roomLivingDataRaw, [JSON.stringify(roomLivingDataRaw)])
  const productList = useMemo(() => productListRaw, [JSON.stringify(productListRaw)])

  const { externalProductList } = useExternalProductData({ roomId, isExternalProductData })

  const { isMobile, isTablet } = useMedia()

  const isShowMobile = isMobile || isTablet

  const [isSendBySelf, setIsSendBySelf] = useState(false)
  const [isBySelf, setIsBySelf] = useState(false)

  const newList = [...micOnList, ...handUpList]

  const handUpUserList: any[] = newList.filter((item, index) => findIndex(newList, { uid: item.uid }) === index)

  const actionData = useVideoAction({
    currentStreamRef: localStreamRef,
    handleCameraClick: () => handleCameraDevice({ socket, userToken, isEnabled: !isCameraEnabled }),
    handleMicClick: () => handleMicDevice({ socket, userToken, isEnabled: !isMicEnabled }),
    role: memberData?.role,
  })

  const isShowPlayer =
    (isLive && !isHost && !isWebRTCConnecting) || (isLive && isConnectReady) || (isFuture && isHost && isLessThenOneHour && isConnectReady)

  const srcWithQuality = handleSrcWithQuality({
    src: liveSourceType === LIVE_SOURCE_TYPE.MP4 ? liveSource : liveUrlInfo[0]?.streamUrl,
    quality: liveSourceType === LIVE_SOURCE_TYPE.MP4 ? '' : (roomData.quality as string),
    sessionRoomId: roomLivingData?.gdata.sessionRoomId,
  })

  useEffect(() => {
    let vbChangeStart = 0
    let vbChangePeriod = 0
    const vbChangeProp: any = vbchange.getBrowserVisibilityProp()

    const onVisibilityChange = () => {
      const isHidden = vbchange.isDomHidden()
      isHidden ? (vbChangeStart = Date.now()) : (vbChangePeriod = diffNowMs(vbChangeStart) / 1000)
      if (!isHidden && vbChangePeriod > 180) {
        //console.log('=== reload page ===')
        //window.location.reload()
      }
    }

    const visibilityChangeHandle = () => {
      document.addEventListener(vbChangeProp, onVisibilityChange, false)
    }
    visibilityChangeHandle()

    const clearVisibilityChangeHandle = () => {
      document.removeEventListener(vbChangeProp, onVisibilityChange)
    }

    return () => {
      clearVisibilityChangeHandle()
    }
  }, [])

  useEffect(() => {
    if (brandName !== VALID_BRAND_NAME.PXEC) return
    if (isShowMobile) return
    if (!roomId || !externalProductList || externalProductList.length === 0) return

    const pids = externalProductList.map((product: any) => product.productUrl.split('/').pop())

    if (pids.length > 0) {
      pxecPostMessages.openProductList(roomId, pids)
    }
  }, [brandName, isShowMobile, roomId, externalProductList])

  useEffect(() => {
    if (!isHost || !isLive) return

    const handleRouteChange = (url: string) => {
      // 因為有切入其他 component 在直播間，例如 User
      // User 切換 tab 時會更改 url，在這種情況下不能導頁
      const asPathWithoutQuery = router.asPath.split('?')[0]
      const urlWithoutQuery = url.split('?')[0]

      const isSamePage = asPathWithoutQuery === urlWithoutQuery

      if (!isSamePage) {
        handleRoutePushWithQuery({ router, pathname: url, isOpen: true })

        throw `App is changing to ${url}`
      }
    }

    router.events.on('routeChangeStart', handleRouteChange)

    return () => {
      router.events.off('routeChangeStart', handleRouteChange)
    }
  }, [isHost, isLive, router])

  // 開始倒數
  useEffect(() => {
    let countdownInterval: any = null

    if (isCountdowning) {
      countdownInterval = setInterval(() => {
        dispatch(setCountdownSecond({ countdownSecond: countdownSecond - 1 }))
      }, 1000)
    } else {
      clearInterval(countdownInterval)

      dispatch(resetCountdownSecond())
    }

    if (countdownSecond <= 0 && isCountdowning) {
      handleMicCancel()

      missingPopupRef.current.setIsPopupOpen(true)
      connectingPopupRef.current.setIsPopupOpen(false)
    }

    return () => {
      clearInterval(countdownInterval)
    }
  }, [dispatch, isCountdowning, countdownSecond])

  const handleHandUpChange = ({ isHandUp }: { isHandUp: boolean }) => {
    handleUserHandUpStatus({ socket, isHandUp: isHandUp, userToken, name: nickName })

    setTimeout(() => {
      getRoomHandUpList({ socket })
    }, 100)
  }

  const handleMicWaitingClick = () => {
    // 主播用
    waitingConnectPopopRef.current.setIsPopupOpen(true)
  }

  const handleHandUpCancel = () => {
    handleUserHandUpStatus({ socket, isHandUp: false, userToken, name: nickName })

    setTimeout(() => {
      getRoomHandUpList({ socket })
    }, 100)

    waitingConnectPopopRef.current.setIsPopupOpen(false)
  }

  const handleMicCancel = () => {
    handleUserConnectStatus({ socket, uid: userToken, isConnect: false, name: '' })

    setTimeout(() => {
      getRoomHandUpList({ socket })
    }, 100)

    dispatch(resetCountdownSecond())

    connectingPopupRef.current.setIsPopupOpen(false)
  }

  const handleMicRequest = () => {
    if (isLogin) {
      if (isValidBrandName) {
        portraitReleaseAgreementPopupRef.current.setIsPopupOpen(true)
      } else {
        checkPopopRef.current.setIsPopupOpen(true)
      }
    } else {
      loginPopupRef.current.setIsPopupOpen(true)
    }
  }

  const getExternalProductData = () => {
    let result: any = productList

    if (isValidBrandName) {
      switch (brandName) {
        case VALID_BRAND_NAME.STRAWBERRY:
          result = strawberryProductList
          break
        case VALID_BRAND_NAME.PCHOME:
          result = externalProductList
          break
        case VALID_BRAND_NAME.ECOSMETICS:
          result = externalProductList
          break
        case VALID_BRAND_NAME.ACMILAN:
          result = externalProductList
          break
        case VALID_BRAND_NAME.FIVE_STAR:
          result = externalProductList
          break
        case VALID_BRAND_NAME.UDESIGN:
          result = externalProductList
          break
        case VALID_BRAND_NAME.EGL_TOURS:
          result = externalProductList
          break
        case VALID_BRAND_NAME.TEN_MART:
          result = externalProductList
          break
        case VALID_BRAND_NAME.SOTHEBYS:
          result = externalProductList
          break
        case VALID_BRAND_NAME.CORNEY_BARROW:
          result = externalProductList
          break
        case VALID_BRAND_NAME.WINNER_PICKLE_BALL:
          result = externalProductList
          break
        case VALID_BRAND_NAME.PACIFIC_PINE_SPORTS:
          result = externalProductList
          break
        case VALID_BRAND_NAME.KB_JEWELS:
          result = externalProductList
          break
        case VALID_BRAND_NAME.KIYAAN_JEWELLERY:
          result = externalProductList
          break
        case VALID_BRAND_NAME.SHOP:
          result = externalProductList
          break
        case VALID_BRAND_NAME.PXEC:
          result = externalProductList
          break
        case VALID_BRAND_NAME.SQUARE_RECORDS:
          result = externalProductList
          break
        case VALID_BRAND_NAME.HKFEC:
          result = externalProductList
          break
        case VALID_BRAND_NAME.XORRO:
          result = externalProductList
          break
        case VALID_BRAND_NAME.AREA02:
          result = externalProductList
          break
        case VALID_BRAND_NAME.GLOBAL_MALL:
          result = externalProductList
          break
        default:
          break
      }
    }

    return result
  }

  const roomCoverData = {
    isPast,
    isFuture,
    coverPhotoUrl,
    hostMemberInfoId,
    title,
    startTime,
    kolName,
  }

  const roomDataProps = {
    socket,
    merchantData,
    productList: getExternalProductData(),
    isWebRTC: ((isHost || isWebRTCConnecting) && isLive) || (isFuture && isHost && isLessThenOneHour),
    isHost,
    role: memberData.role,
    handleMicRequest,
    handleMicWaitingClick,
    isWaitingConnect,
    isMicConnecting,
    handleUserMicCancel: handleMicCancel,
    actionData,
    roomCoverData,
    isLive,
    isPast,
    isFuture,
    isShowPlayer,
    isChatDisabled:
      brandName === VALID_BRAND_NAME.PXEC
        ? isFuture || isPast // PXEC: 不檢查 memberData.isFetched，允許未登入時 focus 觸發彈窗
        : isValidBrandName
        ? !memberData.isFetched || isFuture || isPast
        : isFuture || isPast,
    isBySelf,
    setIsBySelf,
    isLessThenOneHour,
    handUpUserList,
    srcWithQuality,
    ...roomLivingData,
    ...roomData,
  }

  useEffect(() => {
    if (isCountdowning) return

    if (isMicConnecting && !isHost && !isWebRTCConnecting && !isEmpty(hostData)) {
      // 處理買家重整自動連線
      dispatch(setConnectStatus({ connectStatus: CONNECTION_STATUS.CONNECTING }))

      connectingPopupRef.current.setIsPopupOpen(false)
    }
  }, [isMicConnecting, isHost, isWebRTCConnecting, hostData])

  return (
    <div className={cx('room')}>
      <LiveRoomContext.Provider
        value={{
          isSendBySelf,
          setIsSendBySelf,
          isStrawberry,
          brandName,
          isValidBrandName,
          isExternalProductData,
          isWebRTCConnecting,
          isOpenSettingUserNamePopup: settingUserNamePopupRef?.current?.isPopupOpen,
          isRoomLivingDataSettled,
          setIsRoomLivingDataSettled,
          isRoomInfoDataSettled,
          setIsRoomInfoDataSettled,
          settingUserNamePopupRef,
          authToken,
          isHost,
        }}
      >
        {isRoomInfoDataFetched && !isShowMobile && <Desktop {...roomDataProps} />}

        {isRoomInfoDataFetched && isShowMobile && <Mobile {...roomDataProps} isPXEC={brandName === VALID_BRAND_NAME.PXEC} />}
      </LiveRoomContext.Provider>

      {/* Samsung 折疊手機應該要可以正常看，先拿掉再觀察其他手機需不需要加 */}
      {/* {(isIphone || (isAndroid && isMobile)) && !isPortrait && <RotateCover />} */}

      <Popup
        ref={loginPopupRef}
        title={t('LiveRoom_LoginPopup_Mic_Title')}
        confirmLabel={t('LiveRoom_LoginPopup_Confirm')}
        onConfirm={() => {
          handleLoginOrRegister({
            isLogin: true,
            router,
            setUserRecordMutation,
            fixedPlayerData,
            isGoRedirect: true,
            gtmLocation: GTM_LOCATION.LIVE_ROOM,
          })
        }}
        cancelLabel={t('LiveRoom_LoginPopup_Cancel')}
        onCancel={() => loginPopupRef.current.setIsPopupOpen(false)}
        isDrawer
        isHeightAuto
      >
        <p className={cx('room-login')}>{t('LiveRoom_LoginPopup_Mic_Content')}</p>
      </Popup>

      {/* PChome 合作案 */}
      <Popup
        ref={portraitReleaseAgreementPopupRef}
        wrapperClassname={cx('portrait-release-agreement-popup')}
        title={t('LiveRoom_Portrait_Release_Agreement_Popup_Title')}
        hasConfirm={false}
        hasCancel={false}
        canClose={false}
        isDrawer
        isHeightAuto={viewportHeight >= isHeightAutoReferencePoint}
      >
        <PortraitReleaseAgreementPopupContent popupRef={portraitReleaseAgreementPopupRef} checkPopupRef={checkPopopRef} />
      </Popup>

      <Popup
        ref={checkPopopRef}
        title={t('LiveRoom_CheckPopup_Title')}
        confirmLabel={t('LiveRoom_CheckPopup_Confirm')}
        isDisabledConfirm={!hasMediaAccess}
        onConfirm={() => {
          handleHandUpChange({ isHandUp: true })
          getRoomSocketUsers({ socket })

          checkPopopRef.current.setIsPopupOpen(false)
          handleGtmLiveInteraction({
            interactionType: GtmLiveInteractionType.LIVE_CONNECT,
            roomId: roomData.roomId as string,
            hostId: roomData.memberInfoId,
          })
        }}
        cancelLabel={t('LiveRoom_CheckPopup_Cancel')}
        onCancel={() => checkPopopRef.current.setIsPopupOpen(false)}
        isDrawer
        isHeightAuto
        popupClassname={cx('room__interaction-popup')}
      >
        <Preview />
      </Popup>

      <Popup
        ref={waitingConnectPopopRef}
        title={t('LiveRoom_WaitingPopup_Title')}
        confirmLabel={t('LiveRoom_WaitingPopup_Confirm')}
        onConfirm={() => waitingConnectPopopRef.current.setIsPopupOpen(false)}
        cancelLabel={t('LiveRoom_WaitingPopup_Cancel')}
        onCancel={handleHandUpCancel}
        isWrapperWithCancel={false}
        isDrawer
        isHeightAuto
        popupClassname={cx('room__interaction-waiting-host-popup')}
      >
        <div>{t('LiveRoom_WaitingPopup_Content')}</div>
      </Popup>

      <Popup
        ref={connectingPopupRef}
        title={t('LiveRoom_ConnectingPopup_Title')}
        confirmLabel={t('LiveRoom_ConnectingPopup_Confirm')}
        onConfirm={() => {
          dispatch(setConnectStatus({ connectStatus: CONNECTION_STATUS.CONNECTING }))

          connectingPopupRef.current.setIsPopupOpen(false)
        }}
        cancelLabel={t('LiveRoom_ConnectingPopup_Cancel')}
        onCancel={handleMicCancel}
        isOpen={isMicConnecting && connectStatus !== CONNECTION_STATUS.CONNECTING}
        canClose={false}
        isDrawer
        isHeightAuto
        popupClassname={cx('room__interaction-host-request-popup')}
      >
        <div className={cx('room-countdown')}>{countdownSecond}</div>
        <Preview />
      </Popup>

      <Popup
        ref={userOutPopupRef}
        title={t('LiveRoom_UserOutPopup_Title')}
        confirmLabel={t('LiveRoom_UserOutPopup_Confirm')}
        onConfirm={() => {
          dispatch(setIsShowUserOutPopup({ isShowUserOutPopup: false }))
        }}
        isOpen={isShowUserOutPopup}
        hasCancel={false}
        onCancel={() => dispatch(setIsShowUserOutPopup({ isShowUserOutPopup: false }))}
      >
        <p>{t('LiveRoom_UserOutPopup_Content')}</p>
      </Popup>

      <Popup
        ref={missingPopupRef}
        title={t('LiveRoom_MissingPopup_Title')}
        confirmLabel={t('LiveRoom_MissingPopup_Confirm')}
        cancelLabel={t('LiveRoom_MissingPopup_Cancel')}
        onConfirm={() => {
          handleHandUpChange({ isHandUp: true })
          getRoomSocketUsers({ socket })

          missingPopupRef.current.setIsPopupOpen(false)
        }}
        onCancel={() => missingPopupRef.current.setIsPopupOpen(false)}
        isDrawer
        isHeightAuto
        popupClassname={cx('room__interaction-missing-request-popup')}
      ></Popup>

      {/* 品牌直播間輸入暱稱 */}
      <Popup
        ref={settingUserNamePopupRef}
        wrapperClassname={cx('setting-username-popup')}
        title={t('LiveRoom_Portrait_Release_Agreement_Popup_Nickname_Title')}
        hasConfirm={false}
        hasCancel={false}
        canClose={brandName === VALID_BRAND_NAME.PXEC}
      >
        <SettingUserNamePopupContent
          setUsername={setUsername}
          closeSettingUsernamePopup={closeSettingUsernamePopup}
          isLoadingFetchPChomeToken={isLoadingFetchPChomeToken}
          isLoadingUpdateNickname={isLoadingUpdateNickname}
          isErrorFetchPchomeToken={isErrorFetchPchomeToken}
          fetchPchomeTokenErrorData={fetchPchomeTokenErrorData}
          isErrorUpdateNickname={isErrorUpdateNickname}
          updateNicknameErrorData={updateNicknameErrorData}
        />
      </Popup>
    </div>
  )
}

export default LiveRoom
